import * as React from 'react';
import { pageStyles } from "customStyles/common";
import { Paper } from "@mui/material";
import { ConfirmModal, CustomTable, TableTitle, useNoSearchResultsToast } from "../../../common";
import { useCallback, useEffect, useState } from "react";
import { useTableStore, useUserStore } from "../../../state";
import { useExpanded } from "../../../components/layouts/ProtectedLayout";
import { useAudit } from "hooks/useAudit";
import { RbSubAction } from '../../../customEnums/RbSubAction';
import { useRapbackSubscription } from "../../../hooks/useRapbackSubscription";
import { useLocation } from "react-router-dom";
import { extractPageName } from "../../../common/CustomTable/helpers";
import { useRapbackMaintenanceStore } from '../RapbackMaintenance/state/MaintenanceStore'
import { TableNames } from 'customEnums/TableNames';
import { useSetConfig } from 'hooks';

export const RapbackValidations: React.FC = () => {
    const locationObj = useLocation()
    const pageName = extractPageName(locationObj.pathname)
    const { addAuditEvent } = useAudit();

    //Local State
    const [data, setData] = useState()
    const [searched, setSearched] = useState<boolean>(false); //Set true when an advanced form multi parameter search has run
    const [value, setValue] = React.useState<any>([]);
    const [openConfirmModal, setOpenConfirmModal] = useState(false)
    const [confirmModalTitle, setConfirmModalTitle] = useState("")
    const [confirmModalContent, setConfirmModalContent] = useState("")
    const [cancelButtonText, setCancelButtonText] = useState("")
    const [confirmButtonText, setConfirmButtonText] = useState("")
    const [action, setAction] = useState("")
    const [rapbackId, setrapbackId] = useState("")
    const [displaySSN, setDisplaySSN] = useState('')
    const [showSSN, setShowSSN] = useState<boolean>(false);

    //Table Store
    const searchParams = useTableStore((state) => state.searchParams)
    const tableConfig = useTableStore(state => state.tableConfig)
    const simpleSearchRan = useTableStore((state) => state.simpleSearchRan)
    const setDisplayNoSearchResults = useTableStore(state => state.setDisplayNoSearchResults)
    const setPageName = useTableStore(state => state.setPageName)
    const setSearchParams = useTableStore(state => state.setSearchParams)
    const setSimpleSearchRan = useTableStore(state => state.setSimpleSearchRan)
    useNoSearchResultsToast();

    useEffect(() => {
        setPageName('rapback-validations');
        console.log('RapbackValidations mounting - clearing search params');
        setSearchParams([]);
        setSimpleSearchRan(false);
    }, [setPageName, setSearchParams, setSimpleSearchRan]);


    // Rapback Subscription Store:
    const setFullName = useRapbackMaintenanceStore(state => state.setFullName)

    //Hooks
    const { expanded } = useExpanded()
    const userConfig = useUserStore(state => state.userConfig)

    const { userRole } = userConfig;

    //Needed for all pages that use query service
    const handleRenewSubscription = (e, row) => {
        setrapbackId(row.rapbackSubId)
        setAction("renew")
        setConfirmModalTitle("CONFIRM RENEW REQUEST")
        setConfirmModalContent(`Are you sure you want to renew the Rapback Subscription request
        for ${row.fullName}?`)
        setCancelButtonText("CANCEL")
        setConfirmButtonText("RENEW SUBSCRIPTION")
        setOpenConfirmModal(true)
        setFullName(row.fullName)
        addAuditEvent("Subscription Renewal Request Submitted", "Rapback Validation", new Date(), row.rapbackId)
    }

    const handleCancelSubscription = (e, row) => {
        setrapbackId(row.rapbackSubId)
        setAction("cancel_subscription")
        setConfirmModalTitle("CONFIRM CANCEL REQUEST")
        setConfirmModalContent(`Are you sure you want to cancel Rapback Subscriptions
        for ${row.fullName}? This action cannot be undone.`)
        setCancelButtonText("CANCEL")
        setConfirmButtonText("CANCEL SUBSCRIPTION")
        setOpenConfirmModal(true)
        setFullName(row.fullName)
        addAuditEvent("Subscription Cancellation Request Submitted", "Rapback Validation", new Date(), row.rapbackId)
    }

    const actionsConfig: { actionLabel: string, actionHandler: Function }[] = [
        {
            actionLabel: "Renew Subscription",
            actionHandler: handleRenewSubscription
        },
        {
            actionLabel: "Cancel Subscription",
            actionHandler: handleCancelSubscription
        }
    ]

    const { tableHeaderCells, page, rowsPerPage } = useSetConfig(pageName, { page: 1, rowsPerPage: 25 }, TableNames.RAPBACK_VALIDATIONS, {}, actionsConfig)


    //Data
    const { data: mappedData, runCount, totalRecords, loading, fetchMaintenance, refetchParams, handleRenew, handleUnsubscribe } = useRapbackSubscription(
        pageName,
        rowsPerPage,
        page,
        userRole,
        searchParams,
        true,
        TableNames.RAPBACK_VALIDATIONS
    )

    console.log(`debug validations data transformedData mappedData`, mappedData, data)

    const { queryDataRequest } = refetchParams

    useEffect(() => {
        setData(
            mappedData?.map((data: any, index) => {
                return {
                    key: data.key,
                    fullName: data.fullName,
                    dob: data.dob,
                    ssn: data.ssn,
                    oris: data.oris,
                    rapbackSubId: data.rapbackSubId, //is this rapback id?
                    primaryOri: data.primaryOri,
                    subscriptionDate: data.subscriptionDate,
                    expirationDate: data.expirationDate,
                    timeRemaining: data.timeRemaining,
                    chipConfig: {
                        value: data.timeRemaining,
                        style: {
                            borderColor: 'red',
                            color: data.timeRemaining === 1 ? 'white' : 'red',
                            backgroundColor: data.timeRemaining === 1 ? 'red' : 'unset'
                        },
                        variant: data.timeRemaining === 1 ? "filled" : "outlined",
                        label: data.timeRemaining < 1
                            ? "Expired"
                            : `${data.timeRemaining === 1
                                ? "Expires Today"
                                : data.timeRemaining + " " + "Days Remaining"}`,
                        text: data.timeRemaining + " " + "Days Remaining"
                    }
                }
            })
        );
    }, [mappedData?.length, mappedData])


    useEffect(() => {

        console.log('debug stuck search results useEffect: ', { runCount, searchParams, mappedData, simpleSearchRan, searched, setDisplayNoSearchResults: mappedData?.length === 0 && simpleSearchRan || mappedData?.length === 0 && searched })
        console.log('debug stuck search results useEffect: ', { conditional: runCount < 1 && searchParams.length > 0, negativeConditional: !(runCount < 1 && searchParams.length > 0) })
        if (runCount < 1 && searchParams.length > 0) {
            setDisplayNoSearchResults(false)
            return
        }
        if (mappedData?.length === 0 && simpleSearchRan || mappedData?.length === 0 && searched) {
            setDisplayNoSearchResults(true)
        }
    }, [mappedData?.length, simpleSearchRan, searched])

    const handleClickShowSSN = useCallback(async (record: string, e: any) => {
        if (displaySSN !== record) {
            setDisplaySSN(record)
            setShowSSN(!showSSN)

        } else {
            setDisplaySSN('')
            setShowSSN(!showSSN)
        }
    }, [displaySSN])

    const handleConfirmModal = async () => {
        switch (action) {
            case "renew":
                setOpenConfirmModal(false)
                await handleRenew(rapbackId).then(() => {
                    fetchMaintenance(queryDataRequest)
                })
                break;
            case "cancel_subscription":
                setOpenConfirmModal(false)
                await handleUnsubscribe(rapbackId).then(() => {
                    fetchMaintenance(queryDataRequest, RbSubAction.UNSUBSCRIBE)
                })
                break;
        }
    }

    return (
        <>
            <Paper elevation={0} sx={
                {
                    ...pageStyles.pagePaperStyle,
                    maxWidth: `calc(100vw - ${expanded ? "256px" : "64px"})`
                }}>
                <TableTitle name="Rapback Validations" />
                {
                    tableHeaderCells && (
                        <CustomTable
                            //Required props
                            tableConfig={tableConfig}
                            tableName="rapback-validations"
                            loading={loading}
                            rows={data}
                            totalRecords={totalRecords}
                            currentPage={page}
                            pageSize={rowsPerPage}
                            searched={searched}
                            value={value}
                            setValue={setValue}
                            handleClickShowSSN={handleClickShowSSN}
                            displaySSN={displaySSN}
                            setSearched={setSearched}
                            //Per page props
                            defaultSortColumn="expirationDate"
                            rowClickHandler={(e, row) => { }}

                        />)
                }

                <ConfirmModal
                    title={confirmModalTitle}
                    content={confirmModalContent}
                    cancelButtonText={cancelButtonText}
                    confirmButtonText={confirmButtonText}
                    onCancel={() => setOpenConfirmModal(false)}
                    onConfirm={handleConfirmModal}
                    open={openConfirmModal}
                />
            </Paper>{ }
        </>
    )
}
